import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { selectCart } from "../../store/slices/cart/slice";

const LINKS = [
  { to: "/", label: "Početna" },
  { to: "/prodavnica", label: "Prodavnica" },
  { to: "/dostava", label: "Dostava" },
  { to: "/o-nama", label: "O nama" },
];

const formatRSD = (value) =>
  `${new Intl.NumberFormat("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)} RSD`;

const BottomBar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const cart = useSelector(selectCart);

  const btnRefs = useRef({});
  const [pill, setPill] = useState({ left: 0, width: 0, visible: false });
  const [pillReady, setPillReady] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = btnRefs.current[pathname];
      if (el) {
        setPill({ left: el.offsetLeft, width: el.offsetWidth, visible: true });
      } else {
        setPill((p) => ({ ...p, visible: false }));
      }
      requestAnimationFrame(() => setPillReady(true));
    };

    measure();
    // Anton se učitava asinhrono — posle učitavanja fonta širine dugmadi se
    // menjaju, pa pill mora ponovo da se izmeri (fix za hard refresh).
    if (document.fonts?.ready) {
      document.fonts.ready.then(measure);
    }

    // ResizeObserver hvata svaku promenu širine dugmeta (font swap na sporim
    // mrežama ume da stigne i posle fonts.ready) — fix za pill na telefonu.
    let ro;
    const el = btnRefs.current[pathname];
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }

    window.addEventListener("resize", measure);
    window.addEventListener("load", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("load", measure);
      ro?.disconnect();
    };
  }, [pathname]);

  const go = (to) => {
    navigate(to);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  const cartCount = cart.data.reduce((acc, item) => acc + item.quantity, 0);

  const cartTotal = cart.data.reduce((acc, item) => {
    const priceToUse =
      item.salePrice !== undefined &&
      item.salePrice !== null &&
      item.salePrice !== ""
        ? Number(item.salePrice)
        : Number(item.price);

    return acc + priceToUse * item.quantity;
  }, 0);

  return (
    <Bar
      as={motion.nav}
      initial={{ y: 90, x: "-50%", opacity: 0 }}
      animate={{ y: 0, x: "-50%", opacity: 1 }}
      transition={{ delay: 0.25, duration: 0.7, ease: [0.22, 0.72, 0.18, 1] }}
    >
      <Pill
        style={{
          left: pill.left,
          width: pill.width,
          opacity: pill.visible ? 1 : 0,
        }}
        $animate={pillReady}
      />

      <Kanji onClick={() => go("/")}>改善</Kanji>

      {LINKS.map(({ to, label }) => (
        <NavBtn
          key={to}
          ref={(el) => (btnRefs.current[to] = el)}
          $active={pathname === to}
          onClick={() => go(to)}
        >
          <span>{label}</span>
        </NavBtn>
      ))}

      <CartBtn $active={pathname === "/korpa"} onClick={() => go("/korpa")}>
        <Count id="cart-fly-target" $active={pathname === "/korpa"}>
          {cartCount}
        </Count>
        <span>{formatRSD(cartTotal)}</span>
      </CartBtn>
    </Bar>
  );
};

export default BottomBar;

const Bar = styled.nav`
  position: fixed;
  bottom: 2.2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 950;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem;
  border-radius: 10rem;
  background: rgba(16, 16, 19, 0.88);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--line2);
  box-shadow: 0 1.8rem 5rem -1.2rem rgba(0, 0, 0, 0.7);
  white-space: nowrap;

  @media (max-width: 560px) {
    bottom: 1.2rem;
    gap: 0.2rem;
    padding: 0.5rem;
  }
`;

const Pill = styled.span`
  position: absolute;
  top: 0.6rem;
  bottom: 0.6rem;
  border-radius: 10rem;
  background: var(--red, #fe5f55);
  z-index: 0;
  pointer-events: none;
  transition: ${(props) =>
    props.$animate
      ? "left 0.4s var(--ease, ease), width 0.4s var(--ease, ease), opacity 0.3s"
      : "none"};

  @media (max-width: 560px) {
    top: 0.5rem;
    bottom: 0.5rem;
  }
`;

const Kanji = styled.span`
  position: relative;
  z-index: 1;
  font-family: var(--font-kanji);
  color: var(--red);
  font-size: 1.6rem;
  letter-spacing: 0.2rem;
  padding: 0 1rem 0 1.4rem;
  cursor: pointer;
  user-select: none;

  @media (max-width: 480px) {
    display: none;
  }
`;

const NavBtn = styled.button`
  position: relative;
  z-index: 1;
  font-family: "Anton", sans-serif;
  font-size: 1.1rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.6rem;
  border-radius: 10rem;
  border: none;
  cursor: pointer;
  background: transparent;
  color: ${(props) => (props.$active ? "var(--bg0)" : "var(--muted)")};
  transition: color 0.3s var(--ease);

  &:hover {
    color: ${(props) => (props.$active ? "var(--bg0)" : "var(--text)")};
  }

  @media (max-width: 560px) {
    padding: 0.8rem 1.1rem;
  }
`;

const CartBtn = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-family: "Anton", sans-serif;
  font-size: 1.1rem;
  letter-spacing: 0.08em;
  padding: 0.7rem 1.5rem;
  border-radius: 10rem;
  cursor: pointer;
  border: 1px solid rgba(254, 95, 85, 0.4);
  background: ${(props) => (props.$active ? "var(--red)" : "transparent")};
  color: ${(props) => (props.$active ? "var(--bg0)" : "var(--text)")};
  transition: all 0.3s var(--ease);

  &:hover {
    background: ${(props) =>
      props.$active ? "var(--red)" : "rgba(254, 95, 85, 0.1)"};
  }

  @media (max-width: 560px) {
    padding: 0.6rem 1.1rem;
  }
`;

const Count = styled.span`
  font-family: "Poppins-Bold", sans-serif;
  background: ${(props) => (props.$active ? "var(--bg0)" : "var(--red)")};
  color: ${(props) => (props.$active ? "var(--red)" : "var(--bg0)")};
  border-radius: 10rem;
  min-width: 1.9rem;
  height: 1.9rem;
  display: grid;
  place-items: center;
  font-size: 1rem;
  padding: 0 0.5rem;
`;
